import { useEffect, useState } from "react";
import apiClient from "../api/apiClient";
import PageTitle from "../components/common/PageTitle";
import AddButton from "../components/common/AddButton";
import AddItemModal from "../components/modals/AddItemModal";
import { confirmDelete } from "../utils/confirm";
import {
  toastCreate,
  toastDelete,
  toastUpdate,
  showError,
  showWarning,
} from "../utils/toast";
import { Save, X } from "lucide-react";
import DataTable from "../components/common/DataTable";
import { EditButton, DeleteButton } from "../components/common/ActionButtons";
import {
  STORAGE_KEYS,
  CACHE_DURATION,
  saveToSessionStorage,
  getFromSessionStorage,
  invalidateCache,
} from "../utils/sessionStorage";

function getRandomColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form state untuk add
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(getRandomColor());

  // Edit state
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  async function fetchCategories() {
    // Try to get from cache first
    const cachedCategories = getFromSessionStorage(
      STORAGE_KEYS.CATEGORIES,
      CACHE_DURATION.CATEGORIES,
    );

    if (cachedCategories) {
      setCategories(cachedCategories);
      return;
    }

    try {
      const res = await apiClient.get("/categories");
      setCategories(res.data);
      // Save to cache
      saveToSessionStorage(STORAGE_KEYS.CATEGORIES, res.data);
    } catch (err) {
      console.error("Error loading categories:", err);
      showError("Gagal memuat kategori");
    }
  }

  async function handleAddCategory() {
    if (!newName.trim()) {
      showWarning("Nama kategori wajib diisi!");
      return;
    }

    try {
      await toastCreate(
        apiClient.post("/categories", {
          name: newName.trim(),
          color: newColor,
        }),
        newName,
      );

      setShowAddModal(false);
      // Invalidate cache after create
      invalidateCache([STORAGE_KEYS.CATEGORIES]);
      fetchCategories();

      // Reset form
      setNewName("");
      setNewColor(getRandomColor());
    } catch (err) {
      console.error("Add category error:", err);
    }
  }

  function startEdit(category) {
    setEditingId(category.id);
    setEditName(category.name);
    setEditColor(category.color);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  }

  async function saveEdit(id) {
    if (!editName.trim()) {
      showWarning("Nama kategori wajib diisi!");
      return;
    }

    try {
      await toastUpdate(
        apiClient.put(`/categories/${id}`, {
          name: editName.trim(),
          color: editColor,
        }),
        editName,
      );

      // Invalidate cache after update
      invalidateCache([STORAGE_KEYS.CATEGORIES]);
      fetchCategories();
      setEditingId(null);
    } catch (err) {
      console.error("Update category error:", err);
    }
  }

  async function handleDelete(id, name) {
    confirmDelete(`kategori "${name}"`, async () => {
      try {
        await toastDelete(apiClient.delete(`/categories/${id}`), name);
        // Invalidate cache after delete
        invalidateCache([STORAGE_KEYS.CATEGORIES]);
        fetchCategories();
      } catch (err) {
        console.error("Delete category error:", err);
        if (err.response?.status === 409) {
          showError(
            "Kategori tidak bisa dihapus karena masih digunakan di lessons",
          );
        }
      }
    });
  }

  const columns = [
    { field: "index", label: "No", sortable: false, className: "w-20" },
    { field: "name", label: "Nama Kategori", sortable: false },
    { field: "color", label: "Warna", sortable: false, className: "w-40" },
    { field: "actions", label: "Actions", sortable: false, className: "w-32" },
  ];

  return (
    <div className="p-6 pt-20">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-5">
        <PageTitle>Manage Categories</PageTitle>
        <AddButton
          label="Tambah Kategori"
          onClick={() => setShowAddModal(true)}
        />
      </div>

      {/* INFO */}
      <div className="mb-4">
        <p className="text-gray-600">
          Total: <span className="font-semibold">{categories.length}</span>{" "}
          kategori
        </p>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={categories}
        renderRow={(category, idx) => {
          const isEditing = editingId === category.id;
          return (
            <tr key={category.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {idx + 1}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                ) : (
                  <span className="text-sm text-gray-900">{category.name}</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">{editColor}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded border border-gray-300"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="text-sm text-gray-600">
                      {category.color}
                    </span>
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {isEditing ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(category.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Save"
                    >
                      <Save size={18} />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Cancel"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <EditButton onClick={() => startEdit(category)} />
                    <DeleteButton
                      onClick={() => handleDelete(category.id, category.name)}
                      title="Delete"
                    />
                  </div>
                )}
              </td>
            </tr>
          );
        }}
        emptyMessage="Tidak ada kategori"
      />

      {/* Add Modal */}
      <AddItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddCategory}
        title="Tambah Kategori Baru"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nama Kategori *
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Contoh: Safety, Technical, Quality"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Warna Badge
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded cursor-pointer"
              />
              <div className="flex-1">
                <input
                  type="text"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder={newColor}
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
                <p className="text-xs text-gray-500 mt-1">Format: #RRGGBB</p>
              </div>
              <div
                className="w-12 h-12 rounded border border-gray-300"
                style={{ backgroundColor: newColor }}
                title="Preview"
              />
            </div>
          </div>
        </div>
      </AddItemModal>
    </div>
  );
}
